<script>
import api from "@/axios"
import moment from "moment/moment"
import { getBaseUrl } from "@/helpers/baseUrl"
import isMobileMixin from "@/helpers/isMobile"

/**
 * Card expansível de metas exibido na tela de Orçamentos.
 *
 * O backend (`api/metasusuario`) já decide o escopo: admin recebe todas as
 * metas, os demais perfis recebem apenas as do próprio `codrepres`. Aqui só
 * cuidamos da apresentação — inclusive de mostrar todos os campos que vierem
 * preenchidos, já que a integração alimenta um subconjunto diferente de
 * colunas em cada tipo de meta (valor, quantidade, grupo, produto...).
 *
 * `realizado` vem sempre na resposta, mas hoje a integração não preenche o
 * campo: quando nulo a tela mostra "sem apuração" em vez de fingir zero.
 */
export default {
  name: "MetasCard",

  mixins: [isMobileMixin],

  data() {
    return {
      aberto: false,
      carregando: true,
      erro: null,

      admin: false,
      codrepres: null,
      metas: [],

      busca: '',
      represFiltro: 'T',
      statusFiltro: 'T',
    }
  },

  mounted() {
    this.carregaMetas()
  },

  computed: {
    /** Metas após os filtros da barra interna do card. */
    metasFiltradas() {
      const termo = (this.busca ?? '').trim().toLowerCase()

      return (this.metas ?? []).filter((meta) => {
        if (this.represFiltro !== 'T' && (meta.repres ?? '') !== this.represFiltro) {
          return false
        }

        if (this.statusFiltro !== 'T' && (meta.status ?? '') !== this.statusFiltro) {
          return false
        }

        if (!termo) {
          return true
        }

        const alvo = [
          meta.codigo, meta.descricao, meta.obs, meta.tipo,
          meta.repres, meta.repres_nome,
          meta.grupo, meta.grupo_nome,
          meta.produto, meta.produto_nome,
          meta.familia,
        ].filter((v) => v !== null && v !== undefined && v !== '')
            .join(' ')
            .toLowerCase()

        return alvo.includes(termo)
      })
    },

    /** Totais do conjunto filtrado, usados no cabeçalho e na faixa de KPIs. */
    totais() {
      let meta = 0
      let realizado = 0
      let quantidade = 0
      let temRealizado = false

      this.metasFiltradas.forEach((item) => {
        meta += this.numero(item.meta)
        quantidade += this.numero(item.quantidade)

        if (item.realizado !== null && item.realizado !== undefined && item.realizado !== '') {
          temRealizado = true
          realizado += this.numero(item.realizado)
        }
      })

      return {
        qtd: this.metasFiltradas.length,
        meta,
        realizado,
        quantidade,
        temRealizado,
        saldo: meta - realizado,
        percentual: meta > 0 ? (realizado / meta) * 100 : 0,
      }
    },

    /**
     * Opções do filtro de representante (só faz sentido para admin).
     *
     * Metas sem representante entram como uma opção própria: o admin recebe
     * todas as metas da base e precisa conseguir isolar essas também.
     */
    represOpcoes() {
      const mapa = new Map()
      let temSemRepres = false

      ;(this.metas ?? []).forEach((meta) => {
        const codigo = meta.repres ?? ''

        if (codigo === '') {
          temSemRepres = true
          return
        }

        if (mapa.has(codigo)) {
          return
        }

        mapa.set(codigo, meta.repres_nome ? `${codigo} - ${meta.repres_nome}` : `Representante ${codigo}`)
      })

      const opcoes = Array.from(mapa, ([value, text]) => ({ value, text }))
          .sort((a, b) => a.text.localeCompare(b.text))

      if (temSemRepres) {
        opcoes.push({ value: '', text: 'Sem representante' })
      }

      return opcoes
    },

    /** Opções do filtro de status, montadas a partir do que existe na base. */
    statusOpcoes() {
      const codigos = new Set()

      ;(this.metas ?? []).forEach((meta) => {
        if (meta.status !== null && meta.status !== undefined && meta.status !== '') {
          codigos.add(meta.status)
        }
      })

      return Array.from(codigos)
          .sort()
          .map((value) => ({ value, text: this.nomeStatus(value) }))
    },

    /** Resumo por representante exibido para o admin. */
    resumoRepres() {
      const mapa = new Map()

      this.metasFiltradas.forEach((meta) => {
        const codigo = meta.repres ?? ''
        const chave = codigo === '' ? 'SEM_REPRES' : codigo

        if (!mapa.has(chave)) {
          mapa.set(chave, {
            chave,
            codigo,
            nome: meta.repres_nome ?? null,
            semRepres: codigo === '',
            qtd: 0,
            meta: 0,
            realizado: 0,
            temRealizado: false,
          })
        }

        const linha = mapa.get(chave)
        linha.qtd += 1
        linha.meta += this.numero(meta.meta)

        if (meta.realizado !== null && meta.realizado !== undefined && meta.realizado !== '') {
          linha.temRealizado = true
          linha.realizado += this.numero(meta.realizado)
        }
      })

      return Array.from(mapa.values()).sort((a, b) => b.meta - a.meta)
    },

    subtitulo() {
      if (this.carregando) {
        return 'Carregando metas...'
      }

      if (this.erro) {
        return this.erro
      }

      if (this.admin) {
        return 'Todas as metas cadastradas'
      }

      if (!this.codrepres) {
        return 'Nenhum representante vinculado ao seu usuário'
      }

      const nome = (this.metas[0] ?? {}).repres_nome

      return nome
          ? `Metas do representante ${this.codrepres} - ${nome}`
          : `Metas do representante ${this.codrepres}`
    },
  },

  methods: {
    carregaMetas() {
      this.carregando = true
      this.erro = null

      api.get(`${getBaseUrl()}api/metasusuario`)
          .then((res) => {
            const dados = res.data ?? {}

            // Rota não registrada (cache de rotas desatualizado no Laravel)
            // responde 200 com o HTML do SPA. Sem esta checagem o card mostraria
            // "nenhuma meta" em vez de avisar que a resposta não veio da API.
            if (!Array.isArray(dados.metas)) {
              console.log('resposta inesperada de api/metasusuario')
              console.log(res.data)
              this.metas = []
              this.erro = 'Resposta inesperada ao carregar as metas'
              this.carregando = false
              return
            }

            this.admin = dados.admin === true
            this.codrepres = dados.codrepres ?? null
            this.metas = dados.metas
            this.carregando = false
          })
          .catch((err) => {
            console.log('erro ao carregar metas')
            console.log(err)
            this.metas = []
            this.erro = 'Não foi possível carregar as metas'
            this.carregando = false
          })
    },

    alternar() {
      this.aberto = !this.aberto
    },

    /**
     * Campos complementares da meta: entram na lista apenas os que vieram
     * preenchidos da base, para não poluir o card com colunas vazias.
     */
    camposMeta(meta) {
      const campos = []

      const add = (label, valor) => {
        if (valor === null || valor === undefined || valor === '' || valor === '-') {
          return
        }
        campos.push({ label, valor })
      }

      add('Quantidade', this.temValor(meta.quantidade) ? this.formatQuantidade(meta.quantidade) : null)
      add('Tipo', meta.tipo)
      add('Início', this.formatData(meta.inicio))
      add('Fim', this.formatData(meta.fim))
      add('Data inicial', this.formatData(meta.dataini))
      add('Grupo', this.codigoComNome(meta.grupo, meta.grupo_nome))
      add('Produto', this.codigoComNome(meta.produto, meta.produto_nome))
      add('Família', meta.familia)
      add('Representante', this.codigoComNome(meta.repres, meta.repres_nome))
      add('Empresa', meta.empresa)
      add('Observação', meta.obs)

      return campos
    },

    /** Linha de auditoria do rodapé do card da meta. */
    auditoriaMeta(meta) {
      const partes = []

      if (meta.created_by || meta.created_at) {
        partes.push(`Criada${meta.created_by ? ' por ' + meta.created_by : ''}${meta.created_at ? ' em ' + this.formatDataHora(meta.created_at) : ''}`)
      }

      if (meta.updated_by || meta.updated_at) {
        partes.push(`Alterada${meta.updated_by ? ' por ' + meta.updated_by : ''}${meta.updated_at ? ' em ' + this.formatDataHora(meta.updated_at) : ''}`)
      }

      return partes.join(' · ')
    },

    percentualMeta(meta) {
      const valorMeta = this.numero(meta.meta)

      if (valorMeta <= 0 || !this.temValor(meta.realizado)) {
        return 0
      }

      return (this.numero(meta.realizado) / valorMeta) * 100
    },

    /** Largura da barra: limitada a 100% para não estourar o card. */
    larguraBarra(percentual) {
      if (!isFinite(percentual) || percentual <= 0) {
        return '0%'
      }

      return Math.min(percentual, 100) + '%'
    },

    classeBarra(percentual) {
      if (percentual >= 100) {
        return 'bg-success'
      }
      if (percentual >= 70) {
        return 'bg-info'
      }
      if (percentual > 0) {
        return 'bg-warning'
      }

      return 'bg-secondary'
    },

    nomeStatus(status) {
      switch (status) {
        case 'A':
          return 'Em aberto (A)'
        case 'I':
          return 'Inativa (I)'
        case 'C':
          return 'Cancelada (C)'
        case 'F':
          return 'Finalizada (F)'
      }

      return status ? `Status ${status}` : 'Sem status'
    },

    classeStatus(status) {
      switch (status) {
        case 'A':
          return 'badge-soft-warning'
        case 'F':
          return 'badge-soft-success'
        case 'C':
          return 'badge-soft-danger'
        case 'I':
          return 'badge-soft-secondary'
      }

      return 'badge-soft-primary'
    },

    codigoComNome(codigo, nome) {
      if (codigo === null || codigo === undefined || codigo === '') {
        return null
      }

      return nome ? `${codigo} - ${nome}` : codigo
    },

    temValor(valor) {
      return valor !== null && valor !== undefined && valor !== ''
    },

    numero(valor) {
      const convertido = parseFloat(valor)

      return isNaN(convertido) ? 0 : convertido
    },

    formatMoeda(valor) {
      return this.numero(valor).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },

    formatQuantidade(valor) {
      return this.numero(valor).toLocaleString('pt-BR', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    formatPercentual(valor) {
      return this.numero(valor).toLocaleString('pt-BR', {
        style: 'decimal',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }) + '%'
    },

    formatData(valor) {
      if (!valor) {
        return null
      }

      return moment(valor).format('DD/MM/YYYY')
    },

    formatDataHora(valor) {
      if (!valor) {
        return null
      }

      return moment(valor).format('DD/MM/YYYY HH:mm')
    },
  },
}
</script>

<template>
  <div class="card metas-card" :class="{ 'is-open': aberto }">

    <!-- Cabeçalho sempre visível: resume o conjunto e abre/fecha o card. -->
    <div
        class="metas-card__head"
        role="button"
        tabindex="0"
        :aria-expanded="aberto ? 'true' : 'false'"
        @click="alternar"
        @keydown.enter.prevent="alternar"
        @keydown.space.prevent="alternar">

      <span class="metas-card__icon">
        <i class="bx bx-target-lock"></i>
      </span>

      <div class="metas-card__titles">
        <h5 class="metas-card__title">
          Metas
          <span class="badge badge-soft-primary font-size-11 ms-1" v-if="!carregando">
            {{ metas.length }}
          </span>
        </h5>
        <p class="metas-card__subtitle">{{ subtitulo }}</p>
      </div>

      <div class="metas-card__head-stats" v-if="!carregando && metas.length > 0">
        <div class="metas-card__head-stat">
          <span class="metas-card__head-label">Meta total</span>
          <span class="metas-card__head-value">{{ formatMoeda(totais.meta) }}</span>
        </div>
        <div class="metas-card__head-stat" v-if="!isMobile">
          <span class="metas-card__head-label">Realizado</span>
          <span class="metas-card__head-value">
            {{ totais.temRealizado ? formatMoeda(totais.realizado) : 'sem apuração' }}
          </span>
        </div>
      </div>

      <i class="metas-card__chevron bx" :class="aberto ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
    </div>

    <!-- Corpo expansível -->
    <div class="metas-card__body" v-show="aberto">

      <div class="metas-card__loading" v-if="carregando">
        <i class="bx bx-loader bx-spin font-size-18 me-2"></i>
        Carregando metas...
      </div>

      <div class="metas-card__empty" v-else-if="erro">
        {{ erro }}
        <a href="javascript: void(0);" class="ms-1" @click="carregaMetas">tentar novamente</a>
      </div>

      <div class="metas-card__empty" v-else-if="metas.length === 0">
        Nenhuma meta cadastrada para o seu usuário.
      </div>

      <template v-else>

        <!-- Filtros internos -->
        <div class="metas-card__filtros">
          <input
              v-model="busca"
              type="search"
              class="form-control form-control-sm"
              placeholder="Buscar por código, descrição, produto..."/>

          <select v-model="represFiltro" class="form-select form-select-sm" v-if="admin">
            <option value="T">Todos os representantes</option>
            <option v-for="op in represOpcoes" :key="op.value" :value="op.value">{{ op.text }}</option>
          </select>

          <select v-model="statusFiltro" class="form-select form-select-sm" v-if="statusOpcoes.length > 1">
            <option value="T">Todos os status</option>
            <option v-for="op in statusOpcoes" :key="op.value" :value="op.value">{{ op.text }}</option>
          </select>

          <button type="button" class="btn btn-sm btn-outline-secondary" title="Recarregar metas" @click="carregaMetas">
            <i class="bx bx-revision font-size-16 align-middle"></i>
          </button>
        </div>

        <!-- Faixa de indicadores do conjunto filtrado -->
        <div class="metas-card__kpis">
          <div class="metas-card__kpi">
            <span class="metas-card__kpi-label">Metas</span>
            <span class="metas-card__kpi-value">{{ totais.qtd }}</span>
          </div>
          <div class="metas-card__kpi">
            <span class="metas-card__kpi-label">Valor das metas</span>
            <span class="metas-card__kpi-value">{{ formatMoeda(totais.meta) }}</span>
          </div>
          <div class="metas-card__kpi">
            <span class="metas-card__kpi-label">Realizado</span>
            <span class="metas-card__kpi-value">
              {{ totais.temRealizado ? formatMoeda(totais.realizado) : '—' }}
            </span>
          </div>
          <div class="metas-card__kpi">
            <span class="metas-card__kpi-label">Saldo</span>
            <span class="metas-card__kpi-value">
              {{ totais.temRealizado ? formatMoeda(totais.saldo) : '—' }}
            </span>
          </div>
          <div class="metas-card__kpi">
            <span class="metas-card__kpi-label">Quantidade</span>
            <span class="metas-card__kpi-value">{{ formatQuantidade(totais.quantidade) }}</span>
          </div>
        </div>

        <div class="metas-card__progress-wrap">
          <div class="progress metas-card__progress">
            <div
                class="progress-bar"
                :class="classeBarra(totais.percentual)"
                :style="{ width: larguraBarra(totais.percentual) }"></div>
          </div>
          <span class="metas-card__progress-text">
            {{ totais.temRealizado ? formatPercentual(totais.percentual) + ' da meta' : 'Realizado ainda não apurado' }}
          </span>
        </div>

        <!-- Resumo por representante (visão do admin) -->
        <div class="metas-card__repres" v-if="admin && resumoRepres.length > 1">
          <span class="metas-card__section-label">Por representante</span>
          <div class="metas-card__repres-list">
            <div class="metas-card__repres-item" v-for="linha in resumoRepres" :key="linha.chave">
              <span class="metas-card__repres-nome">
                {{ linha.semRepres ? 'Sem representante' : (linha.nome || ('Representante ' + linha.codigo)) }}
              </span>
              <span class="metas-card__repres-cod">
                <template v-if="!linha.semRepres">{{ linha.codigo }} · </template>{{ linha.qtd }} meta(s)
              </span>
              <span class="metas-card__repres-valor">{{ formatMoeda(linha.meta) }}</span>
            </div>
          </div>
        </div>

        <!-- Metas -->
        <span class="metas-card__section-label">Metas cadastradas</span>

        <div class="metas-card__empty" v-if="metasFiltradas.length === 0">
          Nenhuma meta encontrada para os filtros selecionados.
        </div>

        <div class="metas-card__list" v-else>
          <div class="metas-card__meta" v-for="meta in metasFiltradas" :key="meta.id">

            <div class="metas-card__meta-head">
              <span class="metas-card__meta-codigo">{{ meta.codigo || 'sem código' }}</span>
              <span class="metas-card__meta-id">#{{ meta.id }}</span>
              <span class="badge badge-pill font-size-11 ms-auto" :class="classeStatus(meta.status)">
                {{ nomeStatus(meta.status) }}
              </span>
            </div>

            <p class="metas-card__meta-desc">{{ meta.descricao || 'Sem descrição' }}</p>

            <div class="metas-card__meta-valores">
              <div class="metas-card__meta-valor">
                <span class="metas-card__kpi-label">Meta</span>
                <span class="metas-card__meta-valor-num">{{ formatMoeda(meta.meta) }}</span>
              </div>
              <div class="metas-card__meta-valor">
                <span class="metas-card__kpi-label">Realizado</span>
                <span class="metas-card__meta-valor-num">
                  {{ temValor(meta.realizado) ? formatMoeda(meta.realizado) : '—' }}
                </span>
              </div>
              <div class="metas-card__meta-valor">
                <span class="metas-card__kpi-label">Saldo</span>
                <span class="metas-card__meta-valor-num">
                  {{ temValor(meta.realizado) ? formatMoeda(numero(meta.meta) - numero(meta.realizado)) : '—' }}
                </span>
              </div>
            </div>

            <div class="progress metas-card__progress metas-card__progress--slim">
              <div
                  class="progress-bar"
                  :class="classeBarra(percentualMeta(meta))"
                  :style="{ width: larguraBarra(percentualMeta(meta)) }"></div>
            </div>
            <span class="metas-card__meta-progress-text">
              {{ temValor(meta.realizado) ? formatPercentual(percentualMeta(meta)) + ' da meta' : 'Sem apuração de realizado' }}
            </span>

            <div class="metas-card__meta-grid">
              <div class="metas-card__meta-campo" v-for="campo in camposMeta(meta)" :key="campo.label">
                <span class="metas-card__kpi-label">{{ campo.label }}</span>
                <span class="metas-card__meta-campo-valor">{{ campo.valor }}</span>
              </div>
            </div>

            <p class="metas-card__meta-auditoria" v-if="auditoriaMeta(meta)">
              {{ auditoriaMeta(meta) }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
